import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useHomes, useUpdateHome, type Home } from "../hooks/useSettings";
import { tap } from "../motion";

// Per-home resident-sheet link config: the original 住客資料 sheet URL (deep-link
// target) plus the tab gid (deep-link anchor). Without the gid, links fall back
// to gid 0 — sheets whose first tab has another id then show Google's
// "the sheet does not exist". Edit values here instead of patching the API by hand.

function HomeRow({ home }: { home: Home }) {
  const update = useUpdateHome(home.id);
  const [sheetUrl, setSheetUrl] = useState(home.sheetUrl ?? "");
  const [sheetGid, setSheetGid] = useState(home.sheetGid ?? "");
  const [saveError, setSaveError] = useState<string | null>(null);

  // Mirror the server values (including placeholder data swapping to fresh).
  useEffect(() => {
    setSheetUrl(home.sheetUrl ?? "");
    setSheetGid(home.sheetGid ?? "");
  }, [home.sheetUrl, home.sheetGid]);

  const save = (patch: { sheetUrl: string | null; sheetGid: string | null }) =>
    update.mutate(patch, {
      onSuccess: () => setSaveError(null),
      onError: (err) => setSaveError(err instanceof Error ? err.message : "Could not save"),
    });

  const dirty = sheetUrl.trim() !== (home.sheetUrl ?? "") || sheetGid.trim() !== (home.sheetGid ?? "");

  return (
    <div className="settings-row home-sheet-row">
      <strong className="home-sheet-name">{home.name}</strong>
      <div className="settings-field">
        <label htmlFor={`sheet-url-${home.id}`}>Sheet URL</label>
        <input
          id={`sheet-url-${home.id}`}
          type="url"
          value={sheetUrl}
          onChange={(e) => setSheetUrl(e.target.value)}
        />
      </div>
      <div className="settings-field home-sheet-gid">
        <label htmlFor={`sheet-gid-${home.id}`}>Tab gid</label>
        <input
          id={`sheet-gid-${home.id}`}
          inputMode="numeric"
          pattern="[0-9]*"
          value={sheetGid}
          onChange={(e) => setSheetGid(e.target.value)}
        />
      </div>
      <div className="settings-actions">
        <motion.button
          type="button"
          className="btn btn-secondary"
          {...tap}
          disabled={!dirty || update.isPending}
          onClick={() => save({ sheetUrl: sheetUrl.trim() || null, sheetGid: sheetGid.trim() || null })}
        >
          Save
        </motion.button>
      </div>
      {saveError ? <p className="error" role="alert">{saveError}</p> : null}
    </div>
  );
}

export function HomeSheetSettings() {
  const homes = useHomes();
  if (!homes.data) return null;
  return (
    <div className="home-sheet-grid">
      {homes.data.map((home) => (
        <HomeRow key={home.id} home={home} />
      ))}
    </div>
  );
}
